import { useState, useEffect, useCallback } from 'react';
import { getCommissionTransactions } from '../api/commissions';
import type { TransactionList, TransactionFilters } from '../types/commission';

/**
 * Hook for fetching commission transactions with optional filters
 */
export function useCommissionTransactions(
  userId: number | null,
  filters?: TransactionFilters
) {
  const [data, setData] = useState<TransactionList | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    if (!userId) {
      setData(undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const transactions = await getCommissionTransactions(userId, filters);
      setData(transactions);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch transactions');
      setData(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [userId, filters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchTransactions,
  };
}
